import { FC, ReactElement, memo, useCallback } from 'react';
import { useIsFetching, useIsMutating, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppMessage } from '@/common/components';
import { AppRoutes } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { messageOnError } from '@/common/utils';

import { HostingAccountsQueryKeys, HostingsQueryKeys, useDeleteHostingsMutation, useGetByIdHostingsQuery } from '@/modules/Hostings';

import { HostingsOverviewView } from './HostingsOverviewView';

interface Props {
  hostingId: string;
}

export const HostingsOverviewContainer: FC<Props> = memo(({ hostingId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();

  const {
    data: hostingData,
    error: hostingError,
    isLoading: isHostingLoading,
    isError: isHostingError
  } = useGetByIdHostingsQuery(hostingId)({
    enabled: !!hostingId
  });

  const { isLoading: isDeleteHostingLoading, mutate: deleteHostingMutation } = useDeleteHostingsMutation(hostingId)({
    onSuccess: async () => {
      appMessageApi.success('Хостинг успішно видалено!');
      navigate(AppRoutes.Hostings);
      await queryClient.refetchQueries([HostingsQueryKeys.GetAll]);
    },
    onError: (error) => {
      messageOnError(error, appMessageApi);
    }
  });

  const isHostingAccountsListFetching = !!useIsFetching({
    queryKey: [HostingAccountsQueryKeys.GetAll, hostingId],
    exact: true
  });

  const isHostingAccountsCreating = !!useIsMutating({
    mutationKey: [HostingAccountsQueryKeys.Create, hostingId],
    exact: true
  });

  const isHostingAccountsEditing = !!useIsMutating({
    mutationKey: [HostingAccountsQueryKeys.Edit, hostingId],
    exact: false
  });

  const isHostingAccountsDeleting = !!useIsMutating({
    mutationKey: [HostingAccountsQueryKeys.Delete, hostingId],
    exact: false
  });

  const handleDeleteHosting = useCallback((): void => {
    deleteHostingMutation();
  }, []);

  return (
    <FallbackedLayout data={ hostingData } isLoading={ isHostingLoading } isError={ isHostingError } error={ hostingError }>
      { (hosting): ReactElement => (
        <HostingsOverviewView
          hosting={ hosting }
          disableButtons={
            isHostingAccountsCreating ||
            isHostingAccountsEditing ||
            isHostingAccountsDeleting ||
            isDeleteHostingLoading ||
            isHostingAccountsListFetching
          }
          loadingButtons={ isDeleteHostingLoading }
          handleDeleteHosting={ handleDeleteHosting }
        />
      ) }
    </FallbackedLayout>
  );
});
